import { memo, useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock, Spinner } from '../../components/Spinner'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { Sentinel } from '../../library/Sentinel'
import { useToast } from '../../library/useToast'
import { Avatar } from '../Avatar'
import { useCrmRequest } from '../CrmRequest'
import {
  actionOf,
  applyPage,
  badgeOf,
  COUNT_PATH,
  countsFrom,
  createdAt,
  ctaOf,
  decrementCounts,
  EMPTY_FEED,
  endpointFor,
  extractList,
  fanIdOf,
  fanView,
  gatewayError,
  isTab,
  isUnread,
  listsOf,
  loadError,
  MARK_READ_BATCH,
  MARK_READ_PATH,
  markRows,
  needsUser,
  overlayRead,
  readIds,
  rememberRead,
  requestBody,
  seedUsers,
  syncReadStore,
  TABS,
  TABS_ORDER_PATH,
  tabsOrder,
  timeLabel,
  USERS_LIST_CHUNK,
  USERS_LIST_PATH,
  userFacingError,
  userId,
  usersFromList,
  cacheUser,
  type CachedUser,
  type Counts,
  type FeedState,
  type Raw,
  type TabId,
  type UserCache,
} from './notificationsData'
import '../../library/library.css'
import '../ofPages.css'
import './notifications.css'

interface Props {
  /** The page is keyed by it (AccountCrm): another account is a fresh page. */
  accountId: string
  canWrite: boolean
  /** Opens the fan's chat on the Chats page. */
  onOpenChat: (fanId: number | string) => void
  /** The phone's bottom tab bar (the CRM pages). */
  mobileNav?: ReactNode
}

interface Feed extends FeedState {
  status: 'loading' | 'ready' | 'error'
  error: string
  /** A next page is on its way. */
  more: boolean
  /** The last next page failed: no more automatic paging (the button retries). */
  moreFailed: boolean
}

const TAB_KEY = 'ofn.tab'
const LOADING: Feed = { ...EMPTY_FEED, status: 'loading', error: '', more: false, moreFailed: false }

function readSavedTab(): TabId {
  try {
    const v = localStorage.getItem(TAB_KEY)
    return isTab(v) ? v : 'all'
  } catch {
    return 'all'
  }
}

const asObj = (v: unknown): Raw => (v && typeof v === 'object' ? (v as Raw) : {})

/**
 * Notifications: the Account's OnlyFans notifications by section (OnlyFans
 * /my/notifications), as the CRM page shows them — fans resolved through
 * users/list, everything shown marked read (CRM-local), tab badges from count.
 */
export function NotificationsPage({ accountId, canWrite, onOpenChat, mobileNav }: Props) {
  const { t, i18n } = useTranslation('ofNotifications')
  const request = useCrmRequest()
  const { say, node: toast } = useToast()
  const mobile = useIsNarrow()

  const [tab, setTabState] = useState<TabId>(readSavedTab)
  const [order, setOrder] = useState<TabId[]>(TABS)
  const [counts, setCounts] = useState<Counts>({})

  // Each tab's feed lives for the page's lifetime (instant tab switches); the
  // ref is the truth async code reads, the state re-renders.
  const feedsRef = useRef<Partial<Record<TabId, Feed>>>({})
  const [feeds, setFeeds] = useState(feedsRef.current)
  const put = useCallback((id: TabId, next: Feed) => {
    feedsRef.current = { ...feedsRef.current, [id]: next }
    setFeeds(feedsRef.current)
  }, [])
  /** Bumped by a reset: a slower answer of an older load of the tab is dropped. */
  const gens = useRef<Partial<Record<TabId, number>>>({})
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const usersRef = useRef<UserCache>({})
  const [users, setUsers] = useState<UserCache>({})
  const addUsers = useCallback((found: UserCache) => {
    if (!Object.keys(found).length) return
    usersRef.current = { ...usersRef.current, ...found }
    setUsers(usersRef.current)
  }, [])
  const asked = useRef(new Set<string>())

  /** Rows that came in unread this visit keep their highlight until a refresh (OnlyFans does). */
  const fresh = useRef(new Set<string>())
  const pending = useRef(new Set<string>())
  const flushing = useRef(false)

  /** The CRM's apiPostWithError: an `ok: false` gateway envelope throws its message. */
  const call = useCallback(
    async (path: string, body: Raw, local = false) => {
      const res = await request<Raw>(path, { method: 'POST', body, local })
      const err = gatewayError(res.data)
      if (err) throw new Error(err)
      return { status: res.status, data: asObj(res.data) }
    },
    [request],
  )

  // ── Badges and tab order ─────────────────────────────────────────────────
  const countsInFlight = useRef<Promise<void> | null>(null)
  const refreshCounts = useCallback(() => {
    if (countsInFlight.current) return countsInFlight.current
    const run = (async () => {
      try {
        const { status, data } = await call(COUNT_PATH, { account_id: accountId })
        if (status === 200 && data.ok !== false && live.current) setCounts(countsFrom(data, accountId))
      } catch {
        // Badges are optional.
      }
    })().finally(() => {
      countsInFlight.current = null
    })
    countsInFlight.current = run
    return run
  }, [call, accountId])

  const loadOrder = useCallback(async () => {
    // tabs-order needs write access to Notifications; read-only keeps the default order.
    if (!canWrite) return
    try {
      const { status, data } = await call(TABS_ORDER_PATH, { account_id: accountId })
      const next = status === 200 && data.ok !== false ? tabsOrder(data) : null
      if (next && live.current) setOrder(next)
    } catch {
      // Optional.
    }
  }, [call, accountId, canWrite])

  // ── Fans (users/list, `m` first, then without param_name) ────────────────
  const fetchUsers = useCallback(
    async (ids: string[], paramName: string) => {
      const out: Raw[] = []
      for (let i = 0; i < ids.length; i += USERS_LIST_CHUNK) {
        const body: Raw = { account_id: accountId, user_ids: ids.slice(i, i + USERS_LIST_CHUNK) }
        if (paramName) body.param_name = paramName
        const { status, data } = await call(USERS_LIST_PATH, body)
        if (status !== 200 || data.ok === false) continue
        out.push(...usersFromList(data))
      }
      return out
    },
    [call, accountId],
  )

  const hydrate = useCallback(
    async (rows: Raw[]) => {
      addUsers(seedUsers(rows, usersRef.current))
      const ids = [...new Set(rows.map(fanIdOf).filter(Boolean))].filter((id) => !asked.current.has(id) && needsUser(usersRef.current, id))
      if (!ids.length) return
      ids.forEach((id) => asked.current.add(id))
      for (const paramName of ['m', '']) {
        const need = ids.filter((id) => needsUser(usersRef.current, id))
        if (!need.length) break
        try {
          const found = await fetchUsers(need, paramName)
          if (!live.current) return
          const next: Record<string, CachedUser> = {}
          for (const u of found) {
            const id = userId(u)
            if (id) next[id] = cacheUser(u, next[id] ?? usersRef.current[id])
          }
          addUsers(next)
        } catch {
          // Try the next param_name; names stay as parsed meanwhile.
        }
      }
    },
    [addUsers, fetchUsers],
  )

  // ── Pages ────────────────────────────────────────────────────────────────
  const load = useCallback(
    async (id: TabId, reset: boolean) => {
      const prev = feedsRef.current[id]
      if (!reset && (!prev || prev.status !== 'ready' || prev.more || !prev.hasMore || prev.stuck)) return
      const gen = reset ? (gens.current[id] = (gens.current[id] ?? 0) + 1) : (gens.current[id] ?? 0)
      const from = reset ? LOADING : prev!
      put(id, reset ? LOADING : { ...from, more: true, moreFailed: false })
      const stale = () => !live.current || gens.current[id] !== gen
      try {
        const body = requestBody(id, { accountId, offset: from.offset, cursor: reset ? null : from.cursor })
        const { status, data } = await call(endpointFor(id), body)
        if (stale()) return
        const cur = reset ? LOADING : (feedsRef.current[id] ?? from)
        if (status === 200 && data.ok !== false) {
          const batch = extractList(data)
          syncReadStore(accountId, batch)
          const next = applyPage(cur, id, data, overlayRead(batch, readIds(accountId)), reset)
          put(id, { ...next, status: 'ready', error: '', more: false, moreFailed: false })
          if (next.stuck) say(t(next.stuck === 'overlap' ? 'stuck.overlapToast' : 'stuck.duplicateToast'))
          void hydrate(next.rows)
          void refreshCounts()
        } else if (reset) {
          put(id, { ...LOADING, status: 'error', error: loadError(data) })
          say(t('errorStatus', { status }))
        } else {
          put(id, { ...cur, more: false, moreFailed: true })
        }
      } catch (e) {
        if (stale()) return
        const msg = e instanceof Error ? e.message : ''
        if (reset) put(id, { ...LOADING, status: 'error', error: userFacingError(msg) })
        else put(id, { ...(feedsRef.current[id] ?? from), more: false, moreFailed: true })
        say(msg || t('error'))
      }
    },
    [accountId, call, put, say, t, hydrate, refreshCounts],
  )

  // The tab's first page, once per page lifetime (a refresh starts it over).
  useEffect(() => {
    if (!feedsRef.current[tab]) void load(tab, true)
  }, [tab, load])

  useEffect(() => {
    void loadOrder()
  }, [loadOrder])

  const pickTab = (id: TabId) => {
    setTabState(id)
    try {
      localStorage.setItem(TAB_KEY, id)
    } catch {
      // Private mode: the tab lasts this visit.
    }
  }

  const refresh = () => {
    // Other tabs refetch when next opened; in-flight answers for them are dropped.
    for (const id of TABS) if (id !== tab) gens.current[id] = (gens.current[id] ?? 0) + 1
    feedsRef.current = {}
    fresh.current = new Set()
    void load(tab, true)
    void loadOrder()
  }

  // ── Mark read: everything shown is read (CRM-local mark-read) ────────────
  const flush = useCallback(async () => {
    if (flushing.current) return
    flushing.current = true
    try {
      while (pending.current.size) {
        const batch = [...pending.current].slice(0, MARK_READ_BATCH)
        batch.forEach((id) => pending.current.delete(id))
        try {
          const { status, data } = await call(MARK_READ_PATH, { account_id: accountId, ids: batch }, true)
          if (status !== 200 || data.ok === false) throw new Error(`HTTP ${status}`)
          if (live.current) void refreshCounts()
        } catch {
          // Kept for the next pass (the next page or tab shown).
          batch.forEach((id) => pending.current.add(id))
          break
        }
      }
    } finally {
      flushing.current = false
    }
  }, [call, accountId, refreshCounts])

  const shownRows = feeds[tab]?.rows
  useEffect(() => {
    if (!shownRows?.length) return
    const seen = new Set<string>()
    const unread = shownRows.filter((n) => {
      const id = n.id == null ? '' : String(n.id)
      if (!id || !isUnread(n) || pending.current.has(id) || seen.has(id)) return false
      seen.add(id)
      return true
    })
    if (unread.length) {
      rememberRead(accountId, unread)
      seen.forEach((id) => {
        fresh.current.add(id)
        pending.current.add(id)
      })
      const all = { ...feedsRef.current }
      for (const id of TABS) {
        const f = all[id]
        if (f?.rows.length) all[id] = { ...f, rows: markRows(f.rows, seen) }
      }
      feedsRef.current = all
      setFeeds(all)
      setCounts((c) => decrementCounts(c, unread))
    }
    if (pending.current.size) void flush()
  }, [shownRows, accountId, flush])

  // ── Render ───────────────────────────────────────────────────────────────
  const feed = feeds[tab]
  const more = useCallback(() => {
    const f = feedsRef.current[tab]
    if (f && !f.moreFailed) void load(tab, false)
  }, [tab, load])
  const openChat = useCallback((fanId: string) => onOpenChat(fanId), [onOpenChat])
  const lang = i18n.language

  let body: ReactNode
  if (!feed || (feed.status === 'loading' && !feed.rows.length)) {
    body = <LoadingBlock label={t('loading')} />
  } else if (feed.status === 'error') {
    body = (
      <div className="ofn-empty" role="alert">
        <FeatherIcon name="alert-triangle" size={26} className="ofn-error-icon" />
        <div className="ofn-empty-title">{t('loadFailed')}</div>
        <div>{feed.error}</div>
        <button type="button" className="ofp-link-btn" onClick={() => void load(tab, true)}>
          {t('retry')}
        </button>
      </div>
    )
  } else if (!feed.rows.length) {
    body = <div className="ofn-empty">{t('empty')}</div>
  } else {
    body = (
      <>
        {feed.rows.map((n, i) => {
          const fid = fanIdOf(n)
          const key = n.id == null ? `i${i}` : String(n.id)
          return <NotifRow key={key} n={n} user={fid ? users[fid] : undefined} fresh={fresh.current.has(key)} lang={lang} t={t} onOpen={openChat} />
        })}
        {feed.stuck ? (
          <div className="ofn-stuck" role="status">
            {t(feed.stuck === 'overlap' ? 'stuck.overlap' : 'stuck.duplicate')}
          </div>
        ) : (
          feed.hasMore && (
            <>
              <div className="ofn-more">
                <button type="button" className="ofn-more-btn" disabled={feed.more} onClick={() => void load(tab, false)}>
                  {feed.more ? <Spinner size={16} label={t('loading')} /> : t('loadMore')}
                </button>
              </div>
              {!feed.more && !feed.moreFailed && <Sentinel onVisible={more} token={feed.rows.length} />}
            </>
          )
        )}
      </>
    )
  }

  const refreshing = feed?.status === 'loading'
  return (
    <div className={`lib ofp ofn ${mobile ? 'lib--m ofn--m' : ''}`}>
      <main className="lib-main ofn-main">
        <div className="ofn-col">
          <header className="lib-main-head ofn-head">
            <h1 className="lib-main-title ofn-title">{t('title')}</h1>
            <span className="lib-spacer" />
            <button
              type="button"
              className={`lib-icon-btn ofn-refresh ${refreshing ? 'om-busy' : ''}`}
              aria-label={t('refresh')}
              title={t('refresh')}
              onClick={refresh}
            >
              <FeatherIcon name="rotate-cw" size={18} />
            </button>
          </header>
          <div className="ofn-tabs-scroll">
            <div className="ofn-tabs" role="tablist" aria-label={t('sections')}>
              {order.map((id) => {
                const badge = badgeOf(counts, id)
                return (
                  <button key={id} type="button" role="tab" aria-selected={id === tab} className={`ofn-tab ${id === tab ? 'is-on' : ''}`} onClick={() => pickTab(id)}>
                    {t(`tab.${id}`)}
                    {badge && (
                      <span className="ofn-badge" aria-label={t('unreadCount', { count: badge === '99+' ? 99 : Number(badge) })}>
                        {badge}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
          <div key={tab} className="ofn-list" role="tabpanel" aria-label={t(`tab.${tab}`)} aria-busy={feed?.status === 'loading' || feed?.more}>
            {body}
          </div>
        </div>
      </main>
      {mobile && mobileNav}
      {toast}
    </div>
  )
}

// ── One notification ────────────────────────────────────────────────────────

interface RowProps {
  n: Raw
  user: CachedUser | undefined
  fresh: boolean
  lang: string
  t: TFunction
  onOpen: (fanId: string) => void
}

const NotifRow = memo(function NotifRow({ n, user, fresh, lang, t, onOpen }: RowProps) {
  const fanId = fanIdOf(n)
  const v = fanView(n, fanId && user ? { [fanId]: user } : {})
  const name = v.name || (fanId ? t('userId', { id: fanId }) : t('user'))
  const action = actionOf(n)
  const actionText = action.text ?? (action.key ? t(`action.${action.key}`, { amount: action.amount }) : '')
  const lists = listsOf(n)
  const cta = ctaOf(n)
  const time = timeLabel(createdAt(n), lang)
  const timeText = !time ? '' : 'date' in time ? time.date : time.key === 'justNow' ? t('time.justNow') : t(`time.${time.key}`, { count: time.count })
  const unread = isUnread(n) || fresh

  const open = fanId ? () => onOpen(fanId) : undefined
  const onKey = (e: KeyboardEvent) => {
    if (!open || e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
    e.preventDefault()
    open()
  }
  return (
    <div
      className={`ofn-item ${unread ? 'is-unread' : ''} ${fanId ? 'is-clickable' : ''}`}
      data-notif-id={n.id == null ? undefined : String(n.id)}
      role={fanId ? 'button' : undefined}
      tabIndex={fanId ? 0 : undefined}
      aria-label={fanId ? `${t('openChat', { name })}: ${actionText}` : undefined}
      onClick={open}
      onKeyDown={open ? onKey : undefined}
    >
      <Avatar user={{ name, avatar: v.avatar }} size={48} />
      <div className="ofn-content">
        <div className="ofn-headline">
          <span className="ofn-name">{name}</span>
          {v.verified && <FeatherIcon name="check-circle" size={14} className="ofn-verified" aria-label={t('verified')} />}
          {v.handle && <span className="ofn-handle">{v.handle}</span>}
        </div>
        {lists.length > 0 && (
          <div className="ofn-lists">
            <span className="ofn-lists-icon" aria-hidden="true">
              ★
            </span>{' '}
            {[t('lists'), ...lists].join(' • ')}
          </div>
        )}
        {actionText && <div className="ofn-action">{actionText}</div>}
        {timeText && <div className="ofn-meta">{timeText}</div>}
        {cta && (
          <div className="ofn-cta">
            <button type="button" className="ofn-cta-btn" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              {t('cta.label')}
            </button>
            <div className="ofn-cta-sub">{cta.price ? t('cta.price', { price: cta.price }) : t('cta.noPrice')}</div>
          </div>
        )}
      </div>
    </div>
  )
})
